import { useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import PrintPdfDropdown from "../../../common/export_documents/PrintPdfDropdown";
import { newSelectStyles } from "../../../common/shared/select.styled";
import { constants } from "../../../common/constants/constants";
import useBodyClass from "../../../common/hooks/useBodyClassName";
import { PageTitle } from "../../../common/PageTitle";
import { isAdminSelector } from "../../../store/auth/selectors";
import chatIcon from "../../../images/newDesign/chatIcon.svg";
import tenants from "../../../images/newDesign/three_tenants.svg";
import payment from "../../../images/newDesign/payment.svg";
import { StyledApartmentReport } from "./ApartmentReport.styled";
import { Payments } from "./Payments";
import { Tenants } from "./Tenants";
import { Notes } from "./Notes";

const tabs = {
    payments: 'payments',
    tenants: 'tenants',
    notes: 'notes'
};

export function ApartmentReport({ match, address }) {
    const { t } = useTranslation();
    const history = useHistory();

    const { buildingID, apartmentNumber } = match.params;

    useBodyClass('report');

    const ltr = useSelector(state => state.common.ltrEnabled);
    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);
    const isAdmin = useSelector(isAdminSelector);

    const [apartmentOptions, setApartmentOptions] = useState([]);
    const [selectedApartment, setSelectedApartment] = useState({});
    const [currentTab, setCurrentTab] = useState(tabs.payments);
    const [parsedData, setParsedData] = useState([]);
    const [titleRow, setTitleRow] = useState([]);
    const [keysToIgnore, setKeysToIgnore] = useState([]);

    const onApartmentChanged = async (newApartmentNumber) => {
        setSelectedApartment(newApartmentNumber);
        history.push(`/${constants.pages.newApartmentReport}/${buildingID}/${newApartmentNumber.apartmentNumber}`);
    };

    useMemo(() => setApartmentOptions(currentBuildingData.apartments), [currentBuildingData]);

    useMemo(() => {
        const apts = currentBuildingData?.apartments ?? [];
        const currentApartment = apts.find(item => item.apartmentNumber === apartmentNumber);
        if (apts.length > 0 && !currentApartment) {
            onApartmentChanged(apts[0]);
        } else {
            setSelectedApartment(currentApartment ?? {});
        }
    }, [currentBuildingData, apartmentNumber]);

    return (
        <StyledApartmentReport ltr={ltr}>
            <PageTitle>
                <PrintPdfDropdown
                    className='report-pdf-icons'
                    buildingID={buildingID}
                    eventLabel="apartmentCashflow"
                    titleString={t('reports.apartment_cashflow_title', {apartmentNumber})}
                    excelTitle={t('reports.apartment_cashflow_title', {apartmentNumber})}
                    reportName={t('titles.apartment_report')}
                    rows={parsedData}
                    titleRow={titleRow}
                    ignoreColumns={keysToIgnore}
                    address={address}
                    pdfIframe={isAdmin}
                />            
            </PageTitle>

            <div className="dropdown-wrapper">
                <Select
                    className='select bllink-text'
                    styles={newSelectStyles}
                    options={apartmentOptions}
                    value={selectedApartment}
                    onChange={onApartmentChanged}
                    isRtl
                />
            </div>

            <div className="page-tabs">
                <div 
                    className={`tab ${currentTab === tabs.payments ? 'selected' : ''}`}
                    onClick={() => setCurrentTab(tabs.payments)}
                >
                    <img src={payment} />
                    {t('reports.payments')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>

                <div 
                    className={`tab ${currentTab === tabs.tenants ? 'selected' : ''}`}
                    onClick={() => setCurrentTab(tabs.tenants)}
                >
                    <img src={tenants} />
                    {t('reports.tenants')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>

                <div 
                    className={`tab ${currentTab === tabs.notes ? 'selected' : ''}`}
                    onClick={() => setCurrentTab(tabs.notes)}
                >
                    <img src={chatIcon} />
                    {t('comments.comments')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>
            </div>

            {currentTab === tabs.payments && (
                <Payments 
                    buildingID={buildingID} 
                    apartmentNumber={apartmentNumber} 
                    parsedData={parsedData}
                    setParsedData={setParsedData}
                    setTitleRow={setTitleRow}
                    setKeysToIgnore={setKeysToIgnore}
                />
            )}
            {currentTab === tabs.tenants && <Tenants buildingID={buildingID} apartmentNumber={apartmentNumber} />}
            {currentTab === tabs.notes && <Notes buildingID={buildingID} apartmentNumber={apartmentNumber} />}
        </StyledApartmentReport>
    )
}
